import FingerprintJS from '@fingerprintjs/fingerprintjs';

// FingerprintJS Agent 타입
type FingerprintAgent = Awaited<ReturnType<typeof FingerprintJS.load>>;

let fpPromise: Promise<FingerprintAgent> | null = null;

/**
 * FingerprintJS 초기화 (무료 버전)
 * API Key 없이 클라이언트 사이드에서만 동작
 */
const initFingerprint = async (): Promise<FingerprintAgent | null> => {
  if (!fpPromise) {
    // 무료 버전 - API Key 없이 사용
    fpPromise = FingerprintJS.load();
  }
  return fpPromise;
};

/**
 * Fingerprint 데이터 수집
 * @returns fingerprint 데이터 또는 null
 */
const getFingerprint = async (): Promise<string | null> => {
  try {
    console.log('[FingerprintJS] 데이터 수집 시작');
    
    const fp = await initFingerprint();
    if (!fp) {
      return null;
    }
    
    const result = await fp.get();
    
    // 수집된 데이터 로깅 (개발 환경에서만)
    const isDev = import.meta.env.DEV || import.meta.env.MODE === 'development';
    const isStaging = import.meta.env.MODE === 'staging';
    if (isDev || isStaging) {
      console.log('[FingerprintJS] 수집된 데이터:', {
        visitorId: result.visitorId,
        confidence: result.confidence,
        components: Object.keys(result.components)
      });
    }
    
    // 안전한 데이터 반환 (민감한 정보 필터링)
    // const safeComponents = {
    //   userAgent: result.components.userAgent?.value,
    //   screen: result.components.screen?.value,
    //   timezone: result.components.timezone?.value,
    //   language: result.components.language?.value,
    //   platform: result.components.platform?.value,
    //   canvas: result.components.canvas?.value,
    //   webgl: result.components.webgl?.value
    // };
    
    // return {
    //   visitorId: result.visitorId,
    //   confidence: result.confidence,
    //   components: safeComponents,
    //   timestamp: new Date().toISOString()
    // };
    return result.visitorId;
  } catch (error) {
    console.error('[FingerprintJS] 데이터 수집 오류:', error);
    return null;
  }
};

/**
 * 간단한 fingerprint 생성 (fallback)
 * FingerprintJS가 실패할 경우 사용
 */
const getSimpleFingerprint = (): {
  visitorId: string;
  confidence: number;
  components: {
    userAgent: string;
    screen: string;
    timezone: string;
    language: string;
    platform: string;
  };
  timestamp: string;
  source: string;
} | null => {
  try {
    return {
      visitorId: `simple_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      confidence: 0.5,
      components: {
        userAgent: navigator.userAgent,
        screen: `${screen.width}x${screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language,
        platform: navigator.platform
      },
      timestamp: new Date().toISOString(),
      source: 'simple'
    };
  } catch (error) {
    console.error('[FingerprintJS] 간단한 fingerprint 생성 오류:', error);
    return null;
  }
};

/**
 * Fingerprint 데이터 수집 (fallback 포함)
 * @returns fingerprint 데이터 또는 null
 */
const getFingerprintWithFallback = async (): Promise<string | {
  visitorId: string;
  confidence: number;
  components: {
    userAgent: string;
    screen: string;
    timezone: string;
    language: string;
    platform: string;
  };
  timestamp: string;
  source: string;
} | null> => {
  // 먼저 FingerprintJS 시도
  const fingerprint = await getFingerprint();
  
  if (fingerprint) {
    return fingerprint;
  }
  
  // 실패 시 간단한 fingerprint 사용
  console.log('[FingerprintJS] FingerprintJS 실패, 간단한 fingerprint 사용');
  return getSimpleFingerprint();
};

/**
 * Fingerprint 데이터 검증
 * @param fingerprint - 검증할 fingerprint 데이터
 * @returns 유효성 여부
 */
const validateFingerprint = (fingerprint: any): boolean => {
  if (!fingerprint) return false;
  
  // 문자열인 경우 (visitorId만 반환된 경우)
  if (typeof fingerprint === 'string') {
    // UNKNOWN 제외
    if (fingerprint === 'UNKNOWN') return false;
    // 빈 문자열 제외
    if (fingerprint.trim() === '') return false;
    // 최소 길이 체크 (10자 이상)
    if (fingerprint.length < 10) {
      console.warn('[FingerprintJS] visitorId가 너무 짧음:', fingerprint);
      return false;
    }
    return true;
  }
  
  // 객체인 경우
  const requiredFields = ['visitorId', 'confidence', 'components'];
  const hasRequiredFields = requiredFields.every(field => fingerprint.hasOwnProperty(field));
  
  if (!hasRequiredFields) {
    console.warn('[FingerprintJS] 필수 필드 누락:', fingerprint);
    return false;
  }
  
  if (!fingerprint.visitorId || fingerprint.visitorId === 'UNKNOWN' || fingerprint.visitorId.trim() === '' || fingerprint.visitorId.length < 10) {
    console.warn('[FingerprintJS] visitorId가 너무 짧음:', fingerprint.visitorId);
    return false;
  }
  
  return true;
};

/**
 * 브라우저 지문 생성 (캐시 미사용 - 항상 새로 생성)
 * @returns 새로 생성된 브라우저 지문 문자열 (항상 반환 보장)
 */
export async function getCachedBrowserFingerprint(): Promise<string> {
  try {
    const result = await getFingerprintWithFallback();
    
    // 문자열인 경우 그대로 반환
    if (typeof result === 'string') {
      if (validateFingerprint(result)) {
        console.log('[FingerprintJS] fingerprint 생성 완료 (문자열):', result.substring(0, 20) + '...');
        return result;
      }
      console.warn('[FingerprintJS] 문자열 fingerprint가 유효하지 않음:', result.substring(0, 50));
    }
    
    // 객체인 경우 visitorId 추출
    if (result && typeof result === 'object' && 'visitorId' in result) {
      const visitorId = result.visitorId;
      if (validateFingerprint(result)) {
        console.log('[FingerprintJS] fingerprint 생성 완료 (객체):', visitorId.substring(0, 20) + '...');
        return visitorId;
      }
      console.warn('[FingerprintJS] 객체 fingerprint가 유효하지 않음:', visitorId?.substring(0, 50));
    }
    
    // 유효하지 않은 경우 fallback 생성
    console.warn('[FingerprintJS] 생성된 fingerprint가 유효하지 않습니다. 재생성');
    const fallback = getSimpleFingerprint();
    if (fallback && validateFingerprint(fallback)) {
      console.log('[FingerprintJS] fallback fingerprint 사용:', fallback.visitorId.substring(0, 20) + '...');
      return fallback.visitorId;
    }
    
    // 최종 fallback (항상 반환 보장)
    const finalFingerprint = `final_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    console.log('[FingerprintJS] 최종 fallback fingerprint 사용:', finalFingerprint);
    return finalFingerprint;
  } catch (error) {
    console.error('[FingerprintJS] getCachedBrowserFingerprint 오류:', error);
    // 에러 발생 시에도 항상 반환
    const errorFingerprint = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    console.log('[FingerprintJS] 에러 fallback fingerprint 사용:', errorFingerprint);
    return errorFingerprint;
  }
}

export { 
  getFingerprint, 
  getSimpleFingerprint, 
  getFingerprintWithFallback,
  validateFingerprint 
};
